import React, { createContext, useContext, useCallback, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface UsageLimit {
  canUse: boolean;
  usage: number;
  limit: number | null;
  remaining?: number;
  unlimited?: boolean;
}

export interface DashboardData {
  subscription: {
    subscribed: boolean;
    plan_type: string;
    current_period_end: string | null;
    is_manual_subscription?: boolean;
    authorized_emails_count?: number;
    authorized_emails_limit?: number;
  };
  userRole: 'admin' | 'user';
  usageLimits: Record<string, UsageLimit>;
  isCompanyPlan: boolean;
  isPremiumPlan: boolean;
  customerNumber: string | null;
  firstName: string | null;
  activeAddons: string[];
}

interface DashboardContextValue extends DashboardData {
  isLoading: boolean;
  invalidateAll: () => void;
  invalidateUsageLimits: () => void;
  getUsageLimit: (featureName: string) => UsageLimit | undefined;
  hasAddon: (planId: string) => boolean;
}

const defaultDashboardData: DashboardData = {
  subscription: {
    subscribed: false,
    plan_type: 'none',
    current_period_end: null
  },
  userRole: 'user',
  usageLimits: {},
  isCompanyPlan: false,
  isPremiumPlan: false,
  customerNumber: null,
  firstName: null,
  activeAddons: []
};

export const DashboardContext = createContext<DashboardContextValue | null>(null);

export const DASHBOARD_QUERY_KEY = ['dashboard-data'] as const;
export const USAGE_LIMITS_QUERY_KEY = ['usage-limits'] as const;

// Company and premium plan detection helper
const isCompanyPlanType = (planType: string): boolean => {
  return ['company_basic', 'company_basic_year', 'company_multi_email', 'company_multi_email_year', 'company_pro', 'company_pro_year', 'admin_unlimited'].includes(planType);
};

const isPremiumPlanType = (planType: string): boolean => {
  return ['premium', 'premium_year', 'admin_unlimited'].includes(planType);
};

// Fetch all dashboard data in one consolidated call
const fetchDashboardData = async (userId: string): Promise<DashboardData> => {
  
  const startTime = performance.now();

  // Run all queries in parallel - including user_database for customerNumber and firstName
  const [userRoleResult, subscriptionResult, userDataResult, addonsResult] = await Promise.all([
    // Check admin role
    supabase.rpc('has_role', { _user_id: userId, _role: 'admin' }),
    // Get subscription data
    supabase.from('subscriptions')
      .select('plan_type, status, current_period_end, is_manual_subscription')
      .eq('user_id', userId)
      .single(),
    // Get user data (customer_number and first_name)
    supabase.from('user_database')
      .select('customer_number, first_name')
      .eq('id', userId)
      .single(),
    // Get active add-ons
    supabase.from('user_addons')
      .select('plan_id')
      .eq('user_id', userId)
      .eq('status', 'active')
  ]);

  const isAdmin = userRoleResult.data === true;
  const userRole = isAdmin ? 'admin' : 'user';

  // Build subscription info
  const subscription = {
    subscribed: subscriptionResult.data?.status === 'active',
    plan_type: subscriptionResult.data?.plan_type || 'none',
    current_period_end: subscriptionResult.data?.current_period_end || null,
    is_manual_subscription: subscriptionResult.data?.is_manual_subscription
  };

  // Determine plan flags
  const isCompanyPlan = isCompanyPlanType(subscription.plan_type);
  const isPremiumPlan = isPremiumPlanType(subscription.plan_type);

  // Extract user data
  const customerNumber = userDataResult.data?.customer_number || null;
  const firstName = userDataResult.data?.first_name || null;
  
  // Extract active add-ons
  const activeAddons = (addonsResult.data || []).map((a: any) => a.plan_id as string);


  return {
    subscription,
    userRole,
    usageLimits: {}, // Usage limits are fetched separately via batch call
    isCompanyPlan,
    isPremiumPlan,
    customerNumber,
    firstName,
    activeAddons
  };
};

// Batch fetch usage limits using new edge function
const fetchUsageLimits = async (features: string[]): Promise<Record<string, UsageLimit>> => {
  
  const startTime = performance.now();

  try {
    // Single batch call instead of multiple parallel calls
    const { data, error } = await supabase.functions.invoke('check-all-usage-limits', {
      body: { features }
    });

    if (error) throw error;


    return data as Record<string, UsageLimit>;
  } catch (err) {
    console.error('[DashboardContext] Error fetching batch usage limits:', err);
    // Return empty limits with canUse: false for all features
    const fallbackLimits: Record<string, UsageLimit> = {};
    features.forEach(feature => {
      fallbackLimits[feature] = { canUse: false, usage: 0, limit: 0 };
    });
    return fallbackLimits;
  }
};

export const DashboardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Main dashboard data query
  const { data: dashboardData, isLoading } = useQuery({
    queryKey: [...DASHBOARD_QUERY_KEY, user?.id],
    queryFn: () => fetchDashboardData(user!.id),
    enabled: !!user,
    staleTime: 5 * 60 * 1000,    // 5 minutes
    gcTime: 30 * 60 * 1000,      // 30 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Usage limits query - single batch call
  const defaultFeatures = ['url_analysis', 'document_analysis', 'deep_fraud_analysis'];
  const { data: usageLimits } = useQuery({
    queryKey: [...USAGE_LIMITS_QUERY_KEY, user?.id],
    queryFn: () => fetchUsageLimits(defaultFeatures),
    enabled: !!user,
    staleTime: 5 * 60 * 1000,    // 5 minutes (increased from 2)
    gcTime: 10 * 60 * 1000,      // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Realtime listeners for subscription and add-on changes
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('dashboard-subscription-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subscriptions',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          
          queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });
          queryClient.invalidateQueries({ queryKey: USAGE_LIMITS_QUERY_KEY });
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_addons',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          
          queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });
          queryClient.invalidateQueries({ queryKey: USAGE_LIMITS_QUERY_KEY });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient]);

  // Invalidation functions
  const invalidateAll = useCallback(() => {
    
    queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: USAGE_LIMITS_QUERY_KEY });
  }, [queryClient]);

  const invalidateUsageLimits = useCallback(() => {
    
    queryClient.invalidateQueries({ queryKey: USAGE_LIMITS_QUERY_KEY });
  }, [queryClient]);

  // Get usage limit for a specific feature
  const getUsageLimit = useCallback((featureName: string): UsageLimit | undefined => {
    return usageLimits?.[featureName];
  }, [usageLimits]);

  // Check if user has a specific add-on
  const hasAddon = useCallback((planId: string): boolean => {
    return dashboardData?.activeAddons?.includes(planId) ?? false;
  }, [dashboardData?.activeAddons]);

  const value = useMemo<DashboardContextValue>(() => ({
    ...(dashboardData ?? defaultDashboardData),
    usageLimits: usageLimits ?? {},
    isLoading,
    invalidateAll,
    invalidateUsageLimits,
    getUsageLimit,
    hasAddon
  }), [dashboardData, usageLimits, isLoading, invalidateAll, invalidateUsageLimits, getUsageLimit, hasAddon]);

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboardContext = (): DashboardContextValue => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboardContext must be used within a DashboardProvider');
  }
  return context;
};

// Hook for getting usage limits - uses cached data from context
export const useAllUsageLimits = () => {
  const { usageLimits, isLoading, invalidateUsageLimits, getUsageLimit } = useDashboardContext();
  
  return {
    limits: usageLimits,
    loading: isLoading,
    getLimit: getUsageLimit,
    invalidate: invalidateUsageLimits
  };
};
